// src/firebase/profile-service.ts
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  getDocs
} from 'firebase/firestore';
import { getAuth, updateProfile } from 'firebase/auth';
import { db } from './firebase-init';

interface ProfileData {
  displayName?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
  photoURL?: string;
  location?: string;
  bio?: string;
  isProfileComplete?: boolean;
  profileType?: 'patient' | 'caregiver' | 'donor';
  isAdmin?: boolean;
  [key: string]: unknown;
}

/**
 * Get user profile data
 */
export const getUserProfile = async (userId: string): Promise<ProfileData> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));

    if (!userDoc.exists()) {
      // noinspection ExceptionCaughtLocallyJS
      throw new Error('User profile not found');
    }

    return userDoc.data();
  } catch (error) {
    throw error;
  }
};

/**
 * Create initial user profile
 */
export const createUserProfile = async (userId: string, initialData: ProfileData): Promise<boolean> => {
  try {
    const userRef = doc(db, 'users', userId);

    // Check if the profile already exists
    const docSnap = await getDoc(userRef);
    if (docSnap.exists()) {
      // Update existing document instead of creating a new one
      await updateDoc(userRef, {
        ...initialData,
        updatedAt: serverTimestamp()
      });
    } else {
      // Create new profile document
      await setDoc(userRef, {
        ...initialData,
        isProfileComplete: false,
        isAdmin: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }

    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (userId: string, profileData: ProfileData): Promise<boolean> => {
  try {
    const userRef = doc(db, 'users', userId);

    // Update Firestore document
    await updateDoc(userRef, {
      ...profileData,
      updatedAt: serverTimestamp()
    });

    // Also update Firebase Auth profile if display name or photo URL is changed
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (currentUser && (profileData.displayName || profileData.photoURL)) {
      await updateProfile(currentUser, {
        displayName: profileData.displayName || currentUser.displayName,
        photoURL: profileData.photoURL || currentUser.photoURL
      });
    }

    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Check if user profile is complete
 */
export const isProfileComplete = async (userId: string): Promise<boolean> => {
  try {
    const userProfile = await getUserProfile(userId);
    
    // First check if isProfileComplete flag is explicitly set to true
    if (userProfile.isProfileComplete === true) {
      console.log('Profile complete: Flag is true');
      return true;
    }
    
    // For debugging - log the profile
    console.log('Checking profile completeness for:', userId);
    console.log('Profile data:', userProfile);
    
    // Otherwise, check required fields
    // Note: We're being a bit more lenient here - only requiring firstName, lastName, and email
    const requiredFields = ['firstName', 'lastName', 'email'];
    
    // Check if all required fields exist and are not empty
    const fieldsComplete = requiredFields.every(field => {
      const fieldValue = userProfile[field as keyof ProfileData];
      const isValid = fieldValue && 
                     typeof fieldValue === 'string' && 
                     (fieldValue as string).trim() !== '';
      
      console.log(`Field ${field}: ${isValid ? 'valid' : 'invalid'}`);
      return isValid;
    });
    
    console.log('Fields complete:', fieldsComplete);
    
    // If all required fields are complete, automatically mark the profile as complete
    if (fieldsComplete) {
      try {
        await markProfileComplete(userId);
        console.log('Marked profile as complete');
      } catch (error) {
        console.warn('Failed to mark profile as complete:', error);
      }
    }
    
    return fieldsComplete;
  } catch (error) {
    console.error('Error checking profile completeness:', error);
    return false;
  }
};

/**
 * Mark user profile as complete
 */
export const markProfileComplete = async (userId: string): Promise<boolean> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isProfileComplete: true,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Ensure user has a profileType set
 */
export const ensureProfileType = async (userId: string): Promise<string> => {
  try {
    const userProfile = await getUserProfile(userId);
    
    // If profile type is already set, just return it
    if (userProfile.profileType) {
      return userProfile.profileType as string;
    }
    
    // Otherwise, set a default type of 'patient' and update the profile
    const defaultType = 'patient';
    const userRef = doc(db, 'users', userId);
    
    await updateDoc(userRef, {
      profileType: defaultType,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Set default profileType '${defaultType}' for user ${userId}`);
    return defaultType;
  } catch (error) {
    console.error('Error ensuring profile type:', error);
    throw error;
  }
};

/**
 * Check if user is an admin
 */
export const isAdmin = async (userId: string): Promise<boolean> => {
  try {
    const userProfile = await getUserProfile(userId);
    return userProfile.isAdmin === true;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Get all users
 */
export const getAllUsers = async (): Promise<ProfileData[]> => {
  try {
    const usersCollection = collection(db, 'users');
    const userSnapshot = await getDocs(usersCollection);
    const userList = userSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return userList;
  } catch (error) {
    throw error;
  }
};

export default {
  getUserProfile,
  createUserProfile,
  updateUserProfile,
  isProfileComplete,
  markProfileComplete,
  ensureProfileType
};
